import React, { useState } from "react";
import {
  AvailableIngredientGroup,
  CustomContentRow,
} from "../../types/contentTools";

interface IngredientGroupsPanelProps {
  groups: AvailableIngredientGroup[];
  customContentRows: CustomContentRow[];
  onUpdatePreferredTemplate: (
    orgIngredientGroupId: string,
    templateId: string | null,
  ) => Promise<void>;
}

/**
 * List the org's ingredient groups and let the operator set each group's
 * preferred content row template (the default template used when the group's
 * ingredient fills a section without an explicit section-link override).
 */
const IngredientGroupsPanel: React.FC<IngredientGroupsPanelProps> = ({
  groups,
  customContentRows,
  onUpdatePreferredTemplate,
}) => {
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [notice, setNotice] = useState<{ type: "success" | "error"; text: string } | null>(
    null,
  );

  if (groups.length === 0) {
    return (
      <div style={{ color: "#718096", fontSize: "13px" }}>
        No ingredient groups for this organization.
      </div>
    );
  }

  const handleChange = (orgIngredientGroupId: string, value: string) => {
    setPending((prev) => new Set(prev).add(orgIngredientGroupId));
    setNotice(null);
    onUpdatePreferredTemplate(orgIngredientGroupId, value || null)
      .then(() => setNotice({ type: "success", text: "Saved." }))
      .catch((e: any) =>
        setNotice({ type: "error", text: e?.message || "Failed to save." }),
      )
      .finally(() =>
        setPending((prev) => {
          const next = new Set(prev);
          next.delete(orgIngredientGroupId);
          return next;
        }),
      );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {groups.map((g) => {
        const selected = g.preferred_content_row_template_id != null
          ? String(g.preferred_content_row_template_id)
          : "";
        return (
          <div
            key={String(g.org_ingredient_group_id)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              padding: "10px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
            }}
          >
            <span style={{ fontSize: "14px", color: "#2D3748" }}>
              {g.group_name}
              <span style={{ color: "#718096", marginLeft: "8px", fontSize: "12px" }}>
                ({g.total_available} available)
              </span>
            </span>
            <select
              value={selected}
              disabled={pending.has(String(g.org_ingredient_group_id))}
              onChange={(e) => handleChange(String(g.org_ingredient_group_id), e.target.value)}
              style={{
                padding: "6px 10px",
                border: "1px solid #e2e8f0",
                borderRadius: "4px",
                fontSize: "13px",
                minWidth: "240px",
              }}
            >
              <option value="">— no preferred template —</option>
              {customContentRows.map((c) => (
                <option key={String(c.id)} value={String(c.id)}>
                  {c.title}
                </option>
              ))}
            </select>
          </div>
        );
      })}
      {notice && (
        <div
          style={{
            fontSize: "12px",
            color: notice.type === "success" ? "#2F855A" : "#C53030",
          }}
        >
          {notice.text}
        </div>
      )}
    </div>
  );
};

export default IngredientGroupsPanel;
